/**
 * AtlasStormHazardModule — STORM HAZARDS, the client (2026-09-24). Rules in lib/storm_hazards.ts: a thunderstorm,
 * a blizzard or a sandstorm slows the walk as EXPOSURE builds out in the open, a blizzard chills (no sprint), a
 * sandstorm blinds (a vignette), a thunderstorm's lightning can land near you (a stagger, a scorch, the crack) —
 * and shelter (any interior, a cave, the water, a ship) or a fire (a camp, a town's fire circle) sheds it. Nothing
 * here can hurt you: no reducer, no health.
 *
 * The HUD: a small chip under the wallet while a hazard is up or exposure is ebbing ("Blizzard — 45 % exposed —
 * find shelter or a fire"), and a canvas vignette for the sand (a canvas: the TOKEN LAW's fillStyle exemption —
 * the sand's colour is weather, not chrome). UI/33.
 * Levers: `__stormHazard()` → the state; `__stormHazard.force(kind, exposure)` pins it for a probe; `.nearMiss()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { groundYIn } from '../lib/grounding.js';
import { impactDecals } from '../lib/impact_decals.js';
import { cameraImpulse, addTrauma } from '../lib/combat_feel.js';
import { sfx, sfxLoop, stopSfxScope } from '../lib/sfx.js';
import {
  hazardKind, severity, stepExposure, speedScale, chilled, sandBlind, nearMissRoll, nearMissSpot, hazardLabel,
  setStormEffect, staggerFor, STAGGER_S, type HazardInput, type HazardKind,
} from '../lib/storm_hazards.js';

interface WeatherLike {
  wind(): { strength: number; kind: string; density: number; temperatureC: number | null };
  getDust(): number;
  strikeFlash?(): void;
}
interface StationLike { worldId: bigint; kind: string; x: number; z: number }
interface CampLike { worldId: bigint; x: number; z: number }

const FIRE_WARM_M = 7;

export class AtlasStormHazardModule implements IModule {
  readonly name = 'AtlasStormHazard';
  private app!: App;
  private exposure = 0;
  private kind: HazardKind = '';
  private sev = 0;
  private chip: HTMLDivElement | null = null;
  private veil: HTMLCanvasElement | null = null;
  private veilLevel = -1;
  private lastT = 0;
  private fireAt = 0;
  private nearFire = false;
  private forced: { kind: HazardKind; exposure: number } | null = null;
  private misses = 0;
  private lastMiss: { x: number; z: number; dist: number } | null = null;
  private lastEmitKind = '';
  private lastEmitExp = -1;

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('StormHazard', Phase.UI, 33, () => this.tick()));
    const chip = document.createElement('div');
    chip.id = 'storm-hazard-chip';
    chip.className = 'ax-chip';
    chip.style.cssText = 'position:fixed;top:62px;left:50%;transform:translateX(-50%);z-index:31;display:none;pointer-events:none;'
      + 'font:12px var(--ax-font);color:var(--ax-warn);border-color:color-mix(in srgb, var(--ax-warn) 45%, var(--ax-line2));'
      + 'background:color-mix(in srgb, var(--ax-bg) 80%, transparent);padding:4px 10px';
    document.body.appendChild(chip);
    this.chip = chip;
    const veil = document.createElement('canvas');
    veil.id = 'storm-sand-veil';
    veil.width = 256; veil.height = 144;
    veil.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;z-index:5;pointer-events:none;display:none';
    document.body.appendChild(veil);
    this.veil = veil;
    const lever = () => ({ kind: this.kind, exposure: +this.exposure.toFixed(3), severity: +this.sev.toFixed(2), nearFire: this.nearFire, misses: this.misses, lastMiss: this.lastMiss, forced: this.forced });
    const w = window as unknown as { __stormHazard?: unknown };
    w.__stormHazard = Object.assign(lever, {
      force: (kind: HazardKind | null, exposure = 1) => { this.forced = kind === null ? null : { kind, exposure }; return lever(); },
      nearMiss: () => { this.nearMiss(); return this.lastMiss; },
    });
  }

  private weather(): WeatherLike | null {
    return (this.app.getModule('Weather') as unknown as WeatherLike | undefined) ?? null;
  }

  private sheltered(): boolean {
    return !!atlas.interiorId || !!atlas.caveId || atlas.submerged || atlas.spaceFlight || atlas.currentWorldId === null;
  }

  private scanFire(now: number): void {
    if (now - this.fireAt < 1000) return;
    this.fireAt = now;
    this.nearFire = false;
    // A fire only sheds STORM exposure: with no hazard on and none left to shed, the walk over every station row
    // (~5 ms a second at the hub in the 2026-10-05 profile) answers nothing.
    if (!this.kind && this.exposure <= 0.001 && !this.forced) return;
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const db = conn.db as unknown as { activityStation?: { iter(): Iterable<StationLike> }; playerCamp?: { iter(): Iterable<CampLike> } };
    const px = atlas.localX, pz = atlas.localZ;
    try {
      for (const c of db.playerCamp?.iter() ?? []) if (c.worldId === wid && Math.hypot(c.x - px, c.z - pz) <= FIRE_WARM_M) { this.nearFire = true; return; }
      for (const s of db.activityStation?.iter() ?? []) if (s.worldId === wid && s.kind === 'fire_circle' && Math.hypot(s.x - px, s.z - pz) <= FIRE_WARM_M + 3) { this.nearFire = true; return; }
    } catch { /* not subscribed */ }
  }

  private tick(): void {
    const now = performance.now();
    const dt = this.lastT ? Math.min(1, (now - this.lastT) / 1000) : 0; // wall clock: a slow frame still ages exposure
    this.lastT = now;
    this.scanFire(now);
    const w = this.weather();
    const wind = w?.wind();
    const input: HazardInput = {
      kind: wind?.kind ?? '', density: wind?.density ?? 0, wind: wind?.strength ?? 0, dust: w?.getDust() ?? 0,
      temperatureC: wind?.temperatureC ?? null, sheltered: this.sheltered(), nearFire: this.nearFire,
    };
    if (this.forced) {
      this.kind = this.forced.kind;
      this.exposure = input.sheltered || input.nearFire ? stepExposure(this.exposure, { ...input, kind: '', dust: 0 }, dt) : this.forced.exposure;
      this.sev = 1;
    } else {
      this.kind = hazardKind(input);
      this.sev = severity(input);
      this.exposure = stepExposure(this.exposure, input, dt);
    }
    const scale = this.exposure > 0.001 ? speedScale(this.exposure, this.sev || 0.6) : 1;
    setStormEffect(scale, chilled(this.kind, this.exposure) && !input.sheltered);
    if (nearMissRoll(this.kind, this.exposure, input.sheltered, dt, Math.random())) this.nearMiss();
    this.drawHud(input);
    if (this.kind !== this.lastEmitKind || Math.abs(this.exposure - this.lastEmitExp) > 0.05) {
      this.lastEmitKind = this.kind;
      this.lastEmitExp = this.exposure;
      bus.emit('atlas:storm-hazard', { kind: this.kind, exposure: this.exposure });
      this.hazardAudio();
    }
  }

  /** G7: the hazard SOUNDS. A roar rises with EXPOSURE (not the sky's own wind): the weather bed already plays these clips for the
   *  sky; this is a second loop in its own scope, so it is the player's exposed-ness you hear — the roar closes in as it builds,
   *  and it falls away as shelter or a fire sheds it. No new asset: it reuses the weather beds (storm wind / snow wind / sandstorm);
   *  a blizzard's is pitched up into a howl (the chill's breath cue — there is no shiver clip). */
  private hazardKey = '';
  private hazardAudio(): void {
    const want = this.kind === 'storm' ? 'weather_storm_wind' : this.kind === 'blizzard' ? 'weather_snow_wind' : this.kind === 'sandstorm' ? 'weather_sandstorm' : '';
    const gain = want ? Math.min(0.45, this.exposure * 0.45) : 0;
    if (this.hazardKey && this.hazardKey !== want) { sfxLoop(this.hazardKey, 'storm-hazard').set(0, 1, 0.8); }
    if (!want || gain < 0.01) { if (this.hazardKey) { stopSfxScope('storm-hazard', 1.2); this.hazardKey = ''; } return; }
    this.hazardKey = want;
    sfxLoop(want, 'storm-hazard').set(gain, want === 'weather_snow_wind' ? 1.25 : 1, 0.8);
  }

  private drawHud(input: HazardInput): void {
    const chip = this.chip;
    if (chip) {
      const show = (this.kind !== '' || this.exposure > 0.05) && atlas.currentWorldId !== null && !atlas.spaceFlight;
      if (!show) { if (chip.style.display !== 'none') chip.style.display = 'none'; }
      else {
        const pct = Math.round(this.exposure * 100);
        const name = hazardLabel(this.kind) || 'Weather easing';
        const advice = input.nearFire ? 'warming by the fire' : input.sheltered ? 'sheltered' : this.kind ? 'find shelter or a fire' : 'the air is calming';
        const chill = chilled(this.kind, this.exposure) && !input.sheltered ? ' · chilled — no sprinting' : '';
        const text = `${name} — ${pct} % exposed — ${advice}${chill}`;
        if (chip.textContent !== text) chip.textContent = text;
        if (chip.style.display !== 'block') chip.style.display = 'block';
      }
    }
    // The sand veil (canvas — weather colour, not chrome).
    const v = this.veil;
    if (!v) return;
    const level = input.sheltered ? 0 : Math.round(sandBlind(this.kind, this.exposure) * 20) / 20;
    if (level === this.veilLevel) return;
    this.veilLevel = level;
    if (level <= 0) { v.style.display = 'none'; return; }
    const ctx = v.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, v.width, v.height);
    const g = ctx.createRadialGradient(v.width / 2, v.height / 2, v.height * 0.18, v.width / 2, v.height / 2, v.width * 0.62);
    g.addColorStop(0, 'rgba(196,150,96,0)');
    g.addColorStop(0.55, `rgba(184,131,74,${(0.35 * level).toFixed(3)})`);
    g.addColorStop(1, `rgba(150,104,58,${(0.92 * level).toFixed(3)})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, v.width, v.height);
    v.style.display = 'block';
  }

  /** A lightning strike lands NEAR you: the flash, the crack at once, a scorch on the ground, a stagger. */
  private nearMiss(): void {
    if (atlas.currentWorldId === null || this.sheltered()) return;
    const spot = nearMissSpot(atlas.localX, atlas.localZ, Math.random(), Math.random());
    this.lastMiss = { x: +spot.x.toFixed(1), z: +spot.z.toFixed(1), dist: +spot.dist.toFixed(1) };
    this.misses++;
    this.weather()?.strikeFlash?.();
    sfx('sfx_thunder_3', { gain: 0.9 });
    staggerFor(STAGGER_S * 1000, performance.now());
    cameraImpulse(-0.03, (Math.random() - 0.5) * 0.04);
    addTrauma(0.35);
    try {
      const conn = atlas.conn;
      if (!conn) return;
      const y = groundYIn(conn, atlas.currentWorldId, spot.x, spot.z);
      if (Number.isFinite(y)) impactDecals(this.app.world.scene).place('blast', new THREE.Vector3(spot.x, y, spot.z), new THREE.Vector3(0, 1, 0), { sizeMul: 1.3, lifeS: 90 });
    } catch { /* no ground here */ }
    bus.emit('atlas:toast', { text: `Lightning struck ${Math.round(spot.dist)} m away`, kind: 'info', ms: 2600 });
  }

  dispose(): void {
    setStormEffect(1, false);
    this.chip?.remove();
    this.veil?.remove();
  }
}
